/**
 * Fleet System Card — identity, compact energy flow, battery status,
 * overnight projection, health flags. Click navigates to /app/property/:id.
 * Energy flow strip aligns with property dashboard: useEnergyFlowLayout (solar-led / grid-led / battery-led).
 */

import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sun, Battery, Home, ChevronRight, Radio, MoreHorizontal, EyeOff, Trash2, Star, Zap, Settings, Tag } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { FleetSystem } from '@/data/fleet-types';
import { ProviderBadge } from '@/components/ui/ProviderBadge';
import { useEnergyFlowLayout } from '@/hooks/useEnergyFlowLayout';
import type { EnergyFlowNode } from '@/hooks/useEnergyFlowLayout';
import { getLoadKwForEnergyFlow } from '@/lib/energy-state';
import { FleetSparkline } from './FleetSparkline';
import type { SparklinePoint, SparklineStatus } from './FleetSparkline';

interface FleetSystemCardProps {
  system: FleetSystem;
  /** Pre-queried sparkline data for this property (from useFleetSparklineData batch). */
  sparklineData?: SparklinePoint[];
  /** Called when user chooses Hide from actions menu */
  onHide?: (propertyId: string) => void;
  /** Called when user chooses Remove; implementer should show confirmation then delete */
  onRemove?: (propertyId: string, name: string) => void;
  /** When set, show Settings button (e.g. Properties page) */
  onSettingsClick?: () => void;
  /** When set, add Edit tags to actions menu */
  onEditTags?: () => void;
}

function ConfidenceLabel({ level }: { level: FleetSystem['confidence'] }) {
  const map = { high: 'High', medium: 'Medium', low: 'Low' } as const;
  const color =
    level === 'high'
      ? 'text-wt-green-primary'
      : level === 'medium'
        ? 'text-wt-amber-primary'
        : 'text-wt-text-muted';
  return <span className={cn('text-xs font-medium', color)}>{map[level]}</span>;
}

export function FleetSystemCard({ system, sparklineData, onHide, onRemove, onSettingsClick, onEditTags }: FleetSystemCardProps) {
  const navigate = useNavigate();
  const { setPropertyScope } = useApp();
  const { id, systemName, location, provider, online, dataStale, lastPollAt, lastPollStatus, lastPollError, hasOvernightForecast, forecastRisk, gridMode, resilienceRisk, resilienceSeverity } = system;
  const isGridPrimary = gridMode === 'grid_primary';
  const hasActions = onHide != null || onRemove != null || onSettingsClick != null || onEditTags != null;
  const { pvKw, battKw, loadKw, gridKw, genKw, batteryState, socPct, batteryCapacityKwh, availableKwh, overnightKwhAtSunrise, confidence } = system;
  const batteryStateOverride =
    batteryState === 'charging' ? 'Charging' : batteryState === 'discharging' ? 'Discharging' : 'Idle';
  const loadKwForFlow = getLoadKwForEnergyFlow(loadKw, gridKw, provider);
  const layout = useEnergyFlowLayout({
    pvKw,
    batteryKw: battKw,
    gridKw,
    loadKw: loadKwForFlow,
    genKw,
    batteryStateOverride,
  });
  const mainRowNodes = layout.nodes.filter(
    (n) => n.visible && (n.id === 'solar' || n.id === 'battery' || n.id === 'home' || n.id === 'grid-import')
  );

  const handleClick = () => {
    setPropertyScope(id, systemName || 'Property');
    navigate(`/app/property/${id}`);
  };

  const pollLabel =
    lastPollAt == null
      ? 'Never polled'
      : lastPollStatus === 'success'
        ? `Polled ${formatDistanceToNow(new Date(lastPollAt), { addSuffix: true })}`
        : `Last poll failed ${formatDistanceToNow(new Date(lastPollAt), { addSuffix: true })}`;

  // Derive sparkline visual status from system state
  const sparklineStatus: SparklineStatus =
    !sparklineData || sparklineData.length === 0
      ? 'empty'
      : !online
        ? 'critical'
        : dataStale
          ? 'stale'
          : (!isGridPrimary && hasOvernightForecast && forecastRisk)
            ? 'warning'
            : 'healthy';

  const hasRisk = !online || dataStale || (!isGridPrimary && hasOvernightForecast && forecastRisk);
  const accentBar =
    !online
      ? 'border-l-[3px] border-l-status-offline/70'
      : dataStale || (!isGridPrimary && hasOvernightForecast && forecastRisk)
        ? 'border-l-[3px] border-l-amber-primary/60'
        : '';

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'cursor-pointer border transition-colors hover:border-border-default hover:bg-wt-bg-tertiary/50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-wt-bg-primary',
        'bg-wt-bg-secondary border-border/50',
        hasRisk && accentBar,
        !isGridPrimary && hasOvernightForecast && forecastRisk && 'border-amber-primary/30 bg-amber-primary/5'
      )}
    >
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-semibold text-wt-text-primary truncate">{systemName}</h3>
            <p className="text-xs text-wt-text-secondary mt-0.5">
              {location.city}, {location.state}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {onSettingsClick != null && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onSettingsClick();
                }}
                title="Settings"
              >
                <Settings className="w-4 h-4 mr-1.5" />
                Settings
              </Button>
            )}
            {hasActions && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground shrink-0"
                    onClick={(e) => e.stopPropagation()}
                    title="Actions"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  {onHide != null && (
                    <DropdownMenuItem onClick={() => onHide(id)}>
                      <EyeOff className="w-4 h-4 mr-2" />
                      Hide
                    </DropdownMenuItem>
                  )}
                  {onRemove != null && (
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => onRemove(id, systemName)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </DropdownMenuItem>
                  )}
                  {onEditTags != null && (
                    <DropdownMenuItem onClick={onEditTags}>
                      <Tag className="w-4 h-4 mr-2" />
                      Edit tags
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem disabled>
                    <Star className="w-4 h-4 mr-2" />
                    Star (coming soon)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {isGridPrimary && (
              <Badge variant="outline" className="text-[10px] font-medium border-wt-solar-gold/50 text-wt-solar-gold">
                Grid-Primary
              </Badge>
            )}
            <ProviderBadge provider={provider} />
            <ChevronRight className="w-4 h-4 text-wt-text-muted" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0 space-y-3">
        {/* Compact energy flow — same layout as property dashboard (solar-led / grid-led / battery-led) */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 py-2 px-3 rounded-md bg-wt-bg-tertiary/80 border border-border-subtle">
          {mainRowNodes.map((node, idx) => (
            <div key={node.id} className="contents">
              {idx > 0 && <div className="h-px w-3 bg-border shrink-0" aria-hidden />}
              <FleetFlowNode node={node} batteryLabel={layout.batteryLabel} />
            </div>
          ))}
        </div>
        {/* Footer: Grid Import/Export + Gen (align with property dashboard) */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-wt-text-secondary mt-1 px-1">
          <span>
            {layout.gridLabel ? `${layout.gridLabel}: ${layout.gridKwDisplay.toFixed(1)} kW` : gridKw !== null ? `Grid: ${layout.gridKwDisplay.toFixed(1)} kW` : 'Grid: Disconnected'}
          </span>
          <span>Gen: {genKw != null ? `${genKw.toFixed(1)} kW` : 'Offline'}</span>
        </div>

        {/* 24h telemetry sparkline — batch-loaded, never per-card */}
        <FleetSparkline
          data={sparklineData ?? []}
          status={sparklineStatus}
          width={240}
          className="w-full"
        />

        {/* Battery status — kWh + % per ENERGY_SEMANTICS (never % alone when capacity known) */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-wt-text-secondary">Battery</span>
          <span className="font-mono-numeric font-medium text-wt-text-primary">
            {batteryCapacityKwh === 0
              ? '0.0 kWh (0%)'
              : availableKwh != null && Number.isFinite(availableKwh)
                ? `${availableKwh.toFixed(1)} kWh (${socPct}%)`
                : `${socPct}%`}
          </span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-wt-text-muted">Status</span>
          <span
            className={cn(
              'font-medium',
              batteryState === 'charging' && 'text-wt-green-primary',
              batteryState === 'discharging' && 'text-wt-red-primary',
              batteryState === 'idle' && 'text-wt-text-secondary'
            )}
          >
            {batteryState === 'charging' ? 'Charging' : batteryState === 'discharging' ? 'Discharging' : 'Idle'}
          </span>
        </div>

        {/* Overnight projection vs Backup Readiness — grid_primary shows backup kWh */}
        <div className="flex items-center justify-between text-xs pt-1 border-t border-border-subtle">
          <span className="text-wt-text-muted">
            {isGridPrimary ? 'Backup readiness' : '~at sunrise'}
          </span>
          <span className="font-mono-numeric text-wt-text-primary">
            {isGridPrimary
              ? `${availableKwh != null ? availableKwh.toFixed(1) : '—'} kWh`
              : hasOvernightForecast
                ? `~${overnightKwhAtSunrise} kWh`
                : 'No forecast'}
          </span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-wt-text-muted">Confidence</span>
          {isGridPrimary ? (
            <span className="text-wt-text-muted">—</span>
          ) : hasOvernightForecast ? (
            <ConfidenceLabel level={confidence} />
          ) : (
            <span className="text-wt-text-muted">—</span>
          )}
        </div>

        {/* Poll status — always shown */}
        <div className="flex items-center justify-between text-xs pt-1 border-t border-border-subtle">
          <span className="text-wt-text-muted flex items-center gap-1">
            <Radio className="w-3 h-3" />
            Poll
          </span>
          <span
            className={cn(
              lastPollStatus === 'success' ? 'text-status-online' : lastPollAt ? 'text-status-offline' : 'text-wt-text-muted'
            )}
          >
            {pollLabel}
          </span>
        </div>

        {/* Health flags (poll-based) */}
        <div className="flex flex-wrap gap-1.5 pt-1">
          {!online && (
            <Badge variant="outline" className="text-[10px] border-status-offline/50 text-status-offline">
              {lastPollError ? `Offline - ${lastPollError}` : 'Offline'}
            </Badge>
          )}
          {dataStale && (
            <Badge variant="outline" className="text-[10px] border-status-stale/50 text-status-stale">
              Data stale
            </Badge>
          )}
          {!isGridPrimary && hasOvernightForecast && forecastRisk && (
            <Badge variant="outline" className="text-[10px] border-amber-primary/50 text-amber-primary">
              Forecast risk
            </Badge>
          )}
          {isGridPrimary && resilienceRisk && (
            <Badge
              variant="outline"
              className={cn(
                'text-[10px]',
                resilienceSeverity === 'critical'
                  ? 'border-destructive/50 text-destructive'
                  : 'border-amber-primary/50 text-amber-primary'
              )}
            >
              {resilienceSeverity === 'critical' ? 'Resilience Critical' : 'Resilience Warning'}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function FleetFlowNode({ node, batteryLabel }: { node: EnergyFlowNode; batteryLabel: 'Charging' | 'Discharging' | 'Idle' }) {
  const isBattery = node.id === 'battery';
  const isSolar = node.id === 'solar';
  const isHome = node.id === 'home';
  const Icon = isSolar ? Sun : isBattery ? Battery : isHome ? Home : Zap;
  const active = node.kw > 0.1 || isBattery;
  const colorClass =
    node.color === 'wt-green-primary' ? 'text-wt-green-primary' :
    node.color === 'destructive' ? 'text-wt-red-primary' :
    node.color === 'wt-amber-primary' ? 'text-wt-amber-primary' :
    node.color === 'wt-solar-gold' ? 'text-wt-solar-gold' :
    'text-wt-text-secondary';
  const valueLine = isBattery
    ? (batteryLabel === 'Idle' ? '—' : `${batteryLabel} ${active ? node.kw.toFixed(1) + ' kW' : ''}`).trim() || '—'
    : (active ? `${node.kw.toFixed(1)} kW` : '—');
  return (
    <div className={cn('flex flex-col items-center gap-0.5 min-w-0 flex-1', node.dimmed && 'opacity-60')}>
      <Icon
        className={cn(
          'w-5 h-5 shrink-0',
          active ? colorClass : 'text-wt-text-muted'
        )}
      />
      <span className="text-[10px] font-medium truncate w-full text-center text-wt-text-secondary">
        {node.label}
      </span>
      <span className={cn('font-mono-numeric text-xs truncate w-full text-center', active && colorClass)}>
        {valueLine}
      </span>
    </div>
  );
}
